import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';
import { ApiError, ApiResponse } from '../types/api';

export interface AgentSessionSummary {
    id: number;
    incident_id: number;
    project_id: number;
    status: 'active' | 'closed' | 'stopped' | 'failed';
    model: string;
    /** A wake is executing right now. */
    running: boolean;
    wake_count: number;
    last_wake_at: string | null;
    next_wake_at: string | null;
    spend_usd: number;
    spend_cap_usd: number;
    close_reason: string;
    created_at: string;
    /** The agent's latest end-of-wake report (else its latest decision):
     *  the one line of "what does the agent think" for badges and popovers. */
    latest_summary: {
        entry_type: 'summary' | 'decision';
        title: string;
        body: string;
        at: string;
    } | null;
}

export type AgentLogEntryType =
    | 'wake'
    | 'observation'
    | 'consideration'
    | 'decision'
    | 'action'
    | 'notification'
    | 'message'
    | 'status'
    | 'summary'
    | 'error'
    /** The model's reasoning summary before a step; collapsed in the UI. */
    | 'thinking';

export interface AgentLogEntry {
    id: number;
    /** The session this entry belongs to. The log spans every session on
     *  the fire (a reopened incident gets a fresh session), so a change of
     *  session_id marks where a new session began. */
    session_id: number;
    entry_type: AgentLogEntryType;
    title: string;
    body: string;
    data: Record<string, unknown>;
    created_at: string;
}

export interface AgentSessionDetail {
    /** The latest session on the fire. */
    session: AgentSessionSummary | null;
    /** Chronological log across every session on the fire, oldest first. */
    entries: AgentLogEntry[];
    /** Whether agent monitoring is configured server-side at all. */
    enabled: boolean;
}

/** Tenant-level agent settings: the entitlement (superuser-managed — see
 *  Admin → Tenant Capabilities) and the organisation's standing orders for the
 *  agent (tenant-admin-managed — see Admin → Incident Agent). */
export interface AgentIncidentsSetting {
    /** Whether the requesting user may change the *entitlement* (superusers
     *  only). Standing orders are editable by any tenant admin. */
    can_edit: boolean;
    allow_agent_incidents: boolean;
    /** Standing orders added to the agent's charter on every wake, for every
     *  project in the organisation. Empty means "charter only". */
    agent_instructions: string;
    /** Server-enforced length cap for the orders. */
    max_instructions_chars: number;
    /** Whether the environment has the feature configured at all. */
    environment_configured: boolean;
}

/** What a PATCH may carry; the server rejects a non-superuser's attempt at
 *  `allow_agent_incidents` regardless of what the client sends. */
export type AgentIncidentsSettingUpdate = Partial<
    Pick<AgentIncidentsSetting, 'allow_agent_incidents' | 'agent_instructions'>
>;

export const useAgentIncidentsSetting = () =>
    useQuery({
        queryKey: ['agent-incidents-setting'],
        queryFn: async () =>
            (
                await api.request<ApiResponse<AgentIncidentsSetting>>(
                    '/api/admin/tenant/agent-incidents'
                )
            ).data,
        refetchOnWindowFocus: false,
    });

export const useUpdateAgentIncidentsSetting = () => {
    const queryClient = useQueryClient();
    return useMutation<
        ApiResponse<AgentIncidentsSetting>,
        ApiError,
        AgentIncidentsSettingUpdate
    >({
        mutationFn: (update) =>
            api.request<ApiResponse<AgentIncidentsSetting>>(
                '/api/admin/tenant/agent-incidents',
                { method: 'PATCH', body: update }
            ),
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['agent-incidents-setting'] });
        },
    });
};

const sessionKey = (projectId: string | undefined, incidentId: string | undefined) => [
    'project',
    projectId,
    'incidents',
    incidentId,
    'agent-session',
];

/**
 * The incident's agent session + activity log. Polls fast while a wake is
 * executing, slower while the session is active but asleep, and not at all
 * once it has ended (or never started).
 */
export const useIncidentAgentSession = (
    projectId: string | undefined,
    incidentId: string | undefined
) =>
    useQuery({
        queryKey: sessionKey(projectId, incidentId),
        queryFn: () =>
            api.request<ApiResponse<AgentSessionDetail>>(
                `/api/projects/${projectId}/incidents/${incidentId}/agent-session`
            ),
        enabled: !!projectId && !!incidentId,
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
            if (query.state.fetchStatus === 'fetching') return false;
            const session = query.state.data?.data?.session;
            if (!session) return false;
            if (session.running) return 3_000;
            if (session.status === 'active') return 20_000;
            return false;
        },
    });

/** incident_id → latest session summary, for badges on the incidents list. */
export const useProjectAgentSessions = (projectId: string | number | undefined) => {
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useQuery({
        queryKey: ['project', idKey, 'agent-sessions'],
        queryFn: () =>
            api.request<
                ApiResponse<{
                    sessions: Record<string, AgentSessionSummary>;
                    enabled: boolean;
                }>
            >(`/api/projects/${idKey}/agent-sessions`),
        enabled: !!idKey,
        refetchOnWindowFocus: false,
        refetchInterval: 60_000,
    });
};

export const useStartAgentSession = (
    projectId: string | number | undefined,
    incidentId: number | string | undefined
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useMutation<
        ApiResponse<{ session: AgentSessionSummary }>,
        ApiError,
        void
    >({
        mutationFn: () =>
            api.post<ApiResponse<{ session: AgentSessionSummary }>>(
                `/api/projects/${idKey}/incidents/${incidentId}/agent-session`,
                {}
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: sessionKey(idKey, String(incidentId)),
            });
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'agent-sessions'],
            });
        },
    });
};

export const useSendAgentMessage = (
    projectId: string | undefined,
    incidentId: string | undefined,
    sessionId: number | undefined
) => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<{ ok: boolean }>, ApiError, string>({
        mutationFn: (text) =>
            api.post<ApiResponse<{ ok: boolean }>>(
                `/api/agent-sessions/${sessionId}/message`,
                { text }
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: sessionKey(projectId, incidentId),
            });
        },
    });
};

export const useStopAgentSession = (
    projectId: string | undefined,
    incidentId: string | undefined,
    sessionId: number | undefined
) => {
    const queryClient = useQueryClient();
    return useMutation<
        ApiResponse<{ session: AgentSessionSummary }>,
        ApiError,
        void
    >({
        mutationFn: () =>
            api.post<ApiResponse<{ session: AgentSessionSummary }>>(
                `/api/agent-sessions/${sessionId}/stop`,
                {}
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: sessionKey(projectId, incidentId),
            });
        },
    });
};
